/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    ICreateActionContext,
    IActionContext,
    IActionInput
} from '@msdyn365-commerce/core';
import { getChannelDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/OrgUnitsDataActions.g';
import { getChannelDeliveryOptionConfigurationAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';

export interface DeliveryOptionContext {
    isShippingEnabled: boolean;
    isPickupEnabled: boolean;
}

/**
 * GetDeliveryOptionContext Input Action
 */
export class GetDeliveryOptionContextInput implements IActionInput {
    private channelId: number;

    constructor(channelId: number) {
        this.channelId = channelId;
    }

    public getCacheKey = () => `DeliveryOptionContext-${this.channelId}`;
    public getCacheObjectType = () => 'DeliveryOptionContext';
    public dataCacheType = (): CacheType => 'application';
}

const createInput = (args: ICreateActionContext): IActionInput => {
    return new GetDeliveryOptionContextInput(args.requestContext.apiSettings.channelId);
};

async function getDeliveryOptionContextAction(input: IActionInput, ctx: IActionContext): Promise<DeliveryOptionContext> {
    const [channelDeliveryOptions, channelDeliveryOptionConfig] = await Promise.all([
        getChannelDeliveryOptionsAsync({ callerContext: ctx }, [ctx.requestContext.apiSettings.channelId]),
        getChannelDeliveryOptionConfigurationAsync({ callerContext: ctx })
    ]);
    const deliveryOptions =
        channelDeliveryOptions.find(group => group.ChannelId === ctx.requestContext.apiSettings.channelId)?.DeliveryOptions ?? [];
    const pickupDeliveryOptions = channelDeliveryOptionConfig.PickupDeliveryModeCodes ?? [];

    let [isShippingEnabled, isPickupEnabled] = [false, false];

    for (var deliveryOption of deliveryOptions) {
        if (deliveryOption.Code && pickupDeliveryOptions.indexOf(deliveryOption.Code) > -1) {
            isPickupEnabled = true;
        } else {
            isShippingEnabled = true;
        }
        if (isShippingEnabled && isPickupEnabled) {
            break;
        }
    }

    return {
        isShippingEnabled: isShippingEnabled,
        isPickupEnabled: isPickupEnabled
    };
}

export default createObservableDataAction({
    action: <IAction<DeliveryOptionContext>>getDeliveryOptionContextAction,
    id: 'DeliveryOptionContext',
    input: createInput
});
